import { Box, Checkbox, Stack, TableCell, Typography } from '@mui/material';
import HighlightedText from 'src/components/HighlightedText';
import Iconify from 'src/components/Iconify';
import StatusButton from 'src/components/StatusButton';
import TableHeadTypography from 'src/components/TableHeadTypography';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import {
  UNMATCHED_FILE_GROUP_STATUS_MIXED,
  getUnmatchedFileStatusLabel,
} from 'src/utils/enums/unmatchedFileStatus';

export const rowKeyOf = group => (group.groupKey == null ? '__null__' : String(group.groupKey));

export const SortableHeaderCell = ({ column, sort, onSort }) => {
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();
  const active = column.sortable && sort?.column === column.sortKey;
  return (
    <TableCell
      align={column.align || 'left'}
      onClick={column.sortable ? () => onSort(column) : undefined}
      sx={{
        py: '8px',
        px: column.width ? 1 : 2,
        width: column.width,
        verticalAlign: 'middle',
      }}>
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent={column.align === 'right' ? 'flex-end' : 'flex-start'}
        sx={{ cursor: column.sortable ? 'pointer' : 'default' }}>
        <TableHeadTypography
          hasSorting={active}
          mediumScreen={mediumScreen}
          largeScreen={largeScreen}
          smallScreen={smallScreen}>
          {column.label}
        </TableHeadTypography>
        {column.sortable ? (
          <Iconify
            icon={
              active
                ? sort.direction === 'asc'
                  ? 'mdi:arrow-up'
                  : 'mdi:arrow-down'
                : 'mdi:swap-vertical'
            }
            width={14}
            height={14}
            color={active ? '#7DFF92' : '#9CA3AF'}
          />
        ) : null}
      </Stack>
    </TableCell>
  );
};

export const HashText = ({ value }) => (
  <Typography
    variant="caption"
    sx={{
      fontSize: '0.68rem',
      color: '#9CA3AF',
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontFamily: 'monospace',
    }}>
    {value || '—'}
  </Typography>
);

export const TextCell = ({ text, searchValue, fallback = '—', muted }) => (
  <Typography
    variant="caption"
    sx={{
      fontSize: '0.72rem',
      color: muted ? '#9CA3AF' : '#F3F4F6',
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      '& .highlight': {
        backgroundColor: '#7DFF92',
        color: 'black',
        fontWeight: 'bold',
        padding: '0 2px',
      },
    }}>
    <HighlightedText text={text} searchValue={searchValue} fallback={fallback} />
  </Typography>
);

const GroupStatusButton = ({ status, onClick }) => (
  <StatusButton
    status={status}
    size="small"
    onClick={e => {
      e.stopPropagation();
      onClick();
    }}
    sx={{ fontSize: '10px', padding: '1px 7px', minWidth: 0 }}>
    {status === UNMATCHED_FILE_GROUP_STATUS_MIXED ? 'Mixed' : getUnmatchedFileStatusLabel(status)}
  </StatusButton>
);

export const GroupStatusCell = ({ selected, status, onToggle, onOpenStatus }) => (
  <TableCell sx={{ py: 0.5, px: 1, width: 132, whiteSpace: 'nowrap' }}>
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box
        className="hover-checkbox"
        sx={{
          opacity: selected ? 1 : 0.35,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 1, '& .MuiSvgIcon-root path': { fill: '#7DFF92' } },
        }}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={onToggle}
          onClick={e => e.stopPropagation()}
          sx={{ '& .MuiSvgIcon-root path': { fill: '#7DFF92' } }}
        />
      </Box>
      <GroupStatusButton status={status} onClick={onOpenStatus} />
    </Stack>
  </TableCell>
);

export const ViewAllFilesLink = ({ onClick, sx }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      background: 'none',
      border: 'none',
      p: 0,
      cursor: 'pointer',
      color: '#87C8E0',
      fontSize: '0.72rem',
      fontWeight: 600,
      '&:hover': { textDecoration: 'underline' },
      ...sx,
    }}>
    <Iconify icon="mdi:arrow-right-thin" width={16} height={16} />
    View all files
  </Box>
);
